import { useState, useEffect } from 'react';
import { message } from 'antd';
import API from '../../../data/api';
import { getTagAssignments, invalidateTagAssignments } from '../../../data/tagAssignments';

/** Loads, adds and removes the tags of one entity (used inline in other screens' tables). */
export function useTagsDisplayController({ modelType, modelId, onTagsChange }) {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [adding, setAdding] = useState(false);

    const api = API.Instance();

    useEffect(() => {
        if (modelType && modelId) {
            fetchTags();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelType, modelId]);

    const fetchTags = async ({ force = false } = {}) => {
        setLoading(true);
        try {
            // Every row of a table shares one request (data/tagAssignments.js); an API without
            // the batch endpoint is asked per row, as before.
            const byItem = await getTagAssignments(modelType, { force });
            const itemTags = byItem ? byItem.get(String(modelId)) || [] : (await api.requestItemTags(modelType, modelId)).tags || [];
            setTags(itemTags);
            if (onTagsChange) {
                onTagsChange(itemTags);
            }
        } catch (error) {
            console.error('Error fetching tags:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTags = async () => {
        try {
            const response = await api.requestTags({ modelType, active: 'true' });
            // Filter out already assigned tags
            const assignedTagIds = tags.map((t) => t.tagId._id);
            const available = (response.tags || []).filter((t) => !assignedTagIds.includes(t._id));
            setAvailableTags(available);
        } catch (error) {
            console.error('Error fetching available tags:', error);
        }
    };

    const handleAddClick = () => {
        fetchAvailableTags();
        setPopoverVisible(true);
    };

    const handleAddTag = async () => {
        if (!selectedTagId) {
            message.warning('Selecciona una etiqueta');
            return;
        }

        setAdding(true);
        try {
            await api.requestAssignTag(selectedTagId, {
                modelType,
                modelIds: [modelId]
            });
            message.success('Etiqueta agregada');
            setSelectedTagId(null);
            setPopoverVisible(false);
            invalidateTagAssignments(modelType);
            fetchTags({ force: true });
        } catch (error) {
            message.error('Error al agregar etiqueta: ' + (error.response?.data?.message || error.message));
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveTag = async (tagId) => {
        try {
            await api.requestRemoveTag(tagId, {
                modelType,
                modelIds: [modelId]
            });
            message.success('Etiqueta removida');
            invalidateTagAssignments(modelType);
            fetchTags({ force: true });
        } catch (error) {
            message.error('Error al remover etiqueta: ' + (error.response?.data?.message || error.message));
        }
    };

    const handlePopoverOpenChange = (visible) => {
        setPopoverVisible(visible);
        if (!visible) setSelectedTagId(null);
    };

    return {
        tags,
        loading,
        availableTags,
        popoverVisible,
        selectedTagId,
        setSelectedTagId,
        adding,
        handleAddClick,
        handleAddTag,
        handleRemoveTag,
        handlePopoverOpenChange
    };
}
